import "@app/App.css";
import { Button } from "@comps/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { Item, ItemKey, Line, NewRecordRequest, Record, SingleFilter } from "@dto/records";
import { Group } from "@dto/views";
import { GRAY_600_COLOR } from "@utils/colors";
import { BASE_URL, PATH_SEPARATOR, SOURCE_SEPARATOR } from "@utils/constants";
import { FieldType, TYPE_ITEMS_FIELD_KEY, TYPE_ITEMS_OBJECT_KEY } from "@dto/fields";
import { allowedToAddRecord, cleanFilterFromCondition, cleanGroupsFromCondition, getDirectPathFromFiltersAndGroups, getFilterValues } from "@utils/filters";
import { getFieldByPathKeys } from "@utils/model";
import { toastError } from "@utils/toasts";
import { TW_AddRecordButton, TW_AddRecordButtonIcon, useHover } from "@utils/twClasses";
import { post } from "@utils/utils";
import { getUndoSessionId } from "@utils/session";
import { UndoLabelCode, useUndoStore } from "@utils/undo";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useDataStore, useModelStore } from "src/store";


interface AddRecordButtonProps {
  parentRowId: string | null;
  values: Map<string, any> | null
  fieldByName: boolean
  className?: string
  style?: React.CSSProperties
  displayText?: boolean
  disabled?: boolean
  tooltip?: string,
  lineHovered?: boolean
}

function AddRecordButton({ parentRowId, values, fieldByName, className, style, displayText, disabled = false, tooltip = undefined, lineHovered = undefined }: AddRecordButtonProps) {

  const selectedView = useModelStore(state => state.selectedView);
  const workspace = useModelStore(state => state.selectedWorkspace);
  const wsLightColor = useModelStore(state => state.wsLightColor);
  const useButtonHover = useHover({ backgroundColor: "white", color: GRAY_600_COLOR, ...style }, { backgroundColor: wsLightColor, color: "black", ...style }, lineHovered);
  const objectInfo = useModelStore(state => state.selectedObjectInfos);
  const { t } = useTranslation();
  const focus = useDataStore(state => state.focus);
  const fetchData = useDataStore(state => state.fetchData);
  const fetchLine = useDataStore(state => state.fetchLine);
  const fieldColumns = useDataStore(state => state.fieldColumns);
  const insertRecordIntoGroup = useDataStore(state => state.insertRecordIntoGroup);
  const metaLines = useDataStore(state => state.metaLines);
  const relationByKeys = useModelStore(state => state.relationByKeys);
  const fieldByKeys = useModelStore(state => state.fieldByKeys);
  const fieldsByPathKeys = getFieldByPathKeys(objectInfo, objectInfo?.fields ?? null, relationByKeys, fieldByKeys);


  const directPathFromFiltersAndGroups = getDirectPathFromFiltersAndGroups(selectedView?.filter, selectedView?.groups ?? null);

  const pathCleanningCondition = (path: string) => {
    const paramSrcsParts = path.split(SOURCE_SEPARATOR);
    if (paramSrcsParts.length !== 2) {
      return false;
    }
    const paramPath = paramSrcsParts[1];
    for (const pathKey of directPathFromFiltersAndGroups) {
      if (path === pathKey) {
        return true;
      }
      const srcsParts = pathKey.split(SOURCE_SEPARATOR);
      if (srcsParts.length !== 2) {
        continue;
      }
      const pathPath = srcsParts[1];
      const pathParts = pathPath.split(PATH_SEPARATOR);
      if (pathParts.length !== 2) {
        continue;
      }
      const linkPart = pathParts[0];
      if (paramPath.startsWith(linkPart)) {
        return false;
      }
    }
    return true;
  }

  const filterCleanningCondition = (singleFilter: SingleFilter) => {
    if (singleFilter.value != null && singleFilter.valueKey != null) {
      return false;
    }
    const pathKey = singleFilter.pathKey;
    return pathCleanningCondition(pathKey);
  }

  const groupsCleanningCondition = (group: Group) => {
    const pathKey = group.pathKey;
    if (!pathKey) {
      return false;
    }
    return pathCleanningCondition(pathKey);
  }

  const cleanedFilter = cleanFilterFromCondition(selectedView?.filter ?? null, filterCleanningCondition);
  const cleanedGroups = cleanGroupsFromCondition(selectedView?.groups ?? null, groupsCleanningCondition);

  const doFilterAllowedCreation = allowedToAddRecord(cleanedFilter, fieldsByPathKeys, cleanedGroups, selectedView?.aggregations ?? new Map(), fieldByKeys);

  const isDisabled = disabled || !doFilterAllowedCreation;


  const addRecord = () => {
    if (isDisabled) {
      return;
    }
    if (!workspace || !objectInfo) {
      console.error("No workspace or object selected");
      return;
    }
    const newValues = new Map<string, any>(values ?? new Map());

    let overridenLinkByValue: Map<string, boolean> | undefined;
    if (selectedView?.filter) {
      const filterValues = getFilterValues(cleanedFilter, fieldsByPathKeys, fieldByKeys);
      const values = filterValues.filterValues;
      overridenLinkByValue = filterValues.overridenLinkByValue;
      if (values.size > 0) {
        values.forEach((value, key) => {
          newValues.set(key, value);
        });
      }
    }
    focus(null);

    const newRecordRequest: NewRecordRequest = {
      workspaceKey: workspace._key,
      objectKey: objectInfo.object._key,
      fieldValues: newValues,
      fieldByName: fieldByName,
      linkByValue: false,
      overridenLinkByValue: overridenLinkByValue,
      sessionId: getUndoSessionId(),
    }
    post<Record>(`${BASE_URL}/data/records`, newRecordRequest).then(newRecord => {
      if (isCompozzError(newRecord)) {
        const cError = newRecord as CompozzError;
        if (cError.i18n) {
          toastError(translate(t, cError.i18n, cError.params));
        } else {
          toastError(cError.message);
        }
      } else {
        const record = newRecord as Record;
        useUndoStore.getState().markUndoable(objectInfo.object._key, UndoLabelCode.create, 1);
        const parentLine = parentRowId !== null ? metaLines.get(parentRowId)?.line : null;
        const parentItemsBySrcFieldKey = new Map<string, Item>();
        if (parentLine?.isLeftGrouped) {
          const childrenNb = parentLine.children?.length ?? 0;
          if (childrenNb === 1) {
            const hasNoChildren = parentLine.children![0].items.every((child) => child.readOnly === true);
            if (hasNoChildren) {
              const firstLine = parentLine.children![0];
              firstLine.items.forEach((item, index) => 
                parentItemsBySrcFieldKey.set(fieldColumns[index].srcField?._key ?? fieldColumns[index].field._key ?? "", item));
            } else {
              parentLine.items.forEach((item, index) => 
                parentItemsBySrcFieldKey.set(fieldColumns[index].srcField?._key ?? fieldColumns[index].field._key ?? "", item));
            }
          }
        }

        const newLine: Line = {
          items: fieldColumns.map((fieldColumn): Item => {
            let parentKey = null;
            let itemKey: ItemKey = { objectKey: objectInfo!.object._key, recordKey: record._key, fieldKey: fieldColumn.field._key };
            if (fieldColumn.srcField) {
              parentKey = { objectKey: objectInfo!.object._key, recordKey: record._key };
              const objectKey = fieldColumn.srcField.typeItems?.get(TYPE_ITEMS_OBJECT_KEY) || "";
              const fieldKey = fieldColumn.srcField.typeItems?.get(TYPE_ITEMS_FIELD_KEY) || "";
              itemKey = { objectKey, fieldKey, recordKey: "" };
            }
            const parentItemValue = parentItemsBySrcFieldKey.get(fieldColumn.srcField?._key ?? fieldColumn.field._key ?? "")?.value ?? null;
            const value = (!fieldColumn.srcField && !fieldColumn.isPivot && fieldColumn.field.type !== FieldType.Link)
              ? (newValues.get(fieldColumn.field._key) ?? (fieldByName ? newValues.get(fieldColumn.field.name) : null) ?? parentItemValue)
              : parentItemValue;
            return { key: itemKey, value, readOnly: fieldColumn.field.readOnly, aggregated: false, parentKey };
          }),
          children: null,
          isLeftGrouped: false,
        };
        const newRowId = insertRecordIntoGroup(parentRowId, newLine);
        focus({ rowId: newRowId, column: displayText ? 0 : 1 });
        fetchLine(newRowId, [record._key], selectedView);
        setTimeout(() => fetchData(), 0);
      }
    })
  }

  const addRecordButton = (
    <Button className={TW_AddRecordButton + (className ? " " + className : "")} onClick={addRecord} disabled={isDisabled} {...useButtonHover}>
      <Plus className={TW_AddRecordButtonIcon + ((useButtonHover.isHovered && !isDisabled) ? " text-black" : " text-gray-400")} />{displayText && t("data.record.add.button")}
    </Button>
  )

  if (!doFilterAllowedCreation || tooltip) {
    return (
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger style={style} asChild>
            {addRecordButton}
          </TooltipTrigger>
          <TooltipContent>
            {tooltip ? tooltip : t("data.record.add.button.disabled")}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
    )
  } else {
    return (
      addRecordButton
    )
  }
}

export default AddRecordButton;

